/**
 * Paleta institucional Hivi (site público + defaults do tenant).
 * Primário = destaque/CTA; secundário = texto/autoridade; demais = detalhes e fundos.
 */

export const HIVI_PALETTE = {
  /** Azul profundo — texto, base, autoridade */
  dark: '#102A43',
  /** Azul elétrico — CTAs, ícones de destaque */
  primary: '#0075FF',
  /** Azul real — contorno, hover mais escuro */
  royal: '#0058E5',
  /** Ciano/céu — favos, backgrounds suaves, ícones secundários */
  sky: '#45B5FF',
  /** Dourado vivo — alta saturação, leitura forte em fundo escuro */
  accent: '#FFC400',
  /** Cinza gelo — fundo SaaS, bordas leves */
  bg: '#F4F8FC',
  /**
   * Superfície suave (ex.: botão secundário em formulários claros).
   * Espelhado em `tailwind.config.js` → `hivi-surface-soft` / `hivi-surface-soft-hover`.
   */
  surfaceSoft: '#E6F1FF',
  surfaceSoftHover: '#D4E8FF',
} as const;

/**
 * Fundo sólido de CTA com texto branco (~16px bold): WCAG 2.1 AA ≥ 4.5:1.
 * `#0075FF` (primary) com branco fica ~4.2:1; usar `royal` para botões preenchidos.
 */
export const HIVI_SOLID_CTA_BG_FOR_WHITE_TEXT = HIVI_PALETTE.royal;

export const DEFAULT_BRAND_COLORS = {
  primary: HIVI_PALETTE.primary,
  secondary: HIVI_PALETTE.dark,
  primaryLight: HIVI_PALETTE.sky,
  primaryDark: HIVI_PALETTE.royal,
  secondaryLight: HIVI_PALETTE.royal,
  secondaryDark: '#0A1929',
  neutral: HIVI_PALETTE.bg,
} as const;

const hexToRgbChannels = (hex: string): string => {
  const normalized = hex.replace('#', '');

  if (normalized.length !== 6) {
    return '0, 0, 0';
  }

  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);

  return `${r}, ${g}, ${b}`;
};

export const DEFAULT_BRAND_CSS_VARIABLES = {
  '--brand-primary': DEFAULT_BRAND_COLORS.primary,
  '--brand-primary-light': DEFAULT_BRAND_COLORS.primaryLight,
  '--brand-primary-dark': DEFAULT_BRAND_COLORS.primaryDark,
  '--brand-secondary': DEFAULT_BRAND_COLORS.secondary,
  '--brand-secondary-light': DEFAULT_BRAND_COLORS.secondaryLight,
  '--brand-secondary-dark': DEFAULT_BRAND_COLORS.secondaryDark,
  '--brand-neutral': DEFAULT_BRAND_COLORS.neutral,
  '--brand-accent': HIVI_PALETTE.accent,
  '--brand-sky': HIVI_PALETTE.sky,
  '--brand-royal': HIVI_PALETTE.royal,
  '--brand-bg': HIVI_PALETTE.bg,
  '--brand-primary-rgb': hexToRgbChannels(DEFAULT_BRAND_COLORS.primary),
  '--brand-secondary-rgb': hexToRgbChannels(DEFAULT_BRAND_COLORS.secondary),
  '--brand-accent-rgb': hexToRgbChannels(HIVI_PALETTE.accent),
  '--brand-sky-rgb': hexToRgbChannels(HIVI_PALETTE.sky),
} as const;

/** Fallback quando o backend não envia cor primária do estabelecimento */
export const DEFAULT_ESTABLISHMENT_PRIMARY = DEFAULT_BRAND_COLORS.primary;

/** Fallback quando o backend não envia cor secundária do estabelecimento */
export const DEFAULT_ESTABLISHMENT_SECONDARY = DEFAULT_BRAND_COLORS.secondary;

const OLD_DEFAULT_PRIMARY = '#8B0000';
const OLD_DEFAULT_SECONDARY = '#333333';

/** Migra tema persistido que ainda usa a paleta antiga (bordô + grafite). */
export function migrateLegacyThemeColors(
  primary: string,
  secondary: string
): {
  primary: string;
  secondary: string;
} {
  const p = primary.trim().toUpperCase();
  const s = secondary.trim().toUpperCase();
  if (p === OLD_DEFAULT_PRIMARY && s === OLD_DEFAULT_SECONDARY) {
    return {
      primary: DEFAULT_BRAND_COLORS.primary,
      secondary: DEFAULT_BRAND_COLORS.secondary,
    };
  }
  return { primary, secondary };
}

/**
 * Retorna as cores do estabelecimento ou as cores padrão Hivi
 */
export const getEstablishmentColors = (
  primaryColor?: string | null,
  secondaryColor?: string | null
) => {
  return {
    primary: primaryColor || DEFAULT_BRAND_COLORS.primary,
    secondary: secondaryColor || DEFAULT_BRAND_COLORS.secondary,
  };
};

/** Alinhado ao loader (`useEstablishmentLoader`): tema white-label só quando o par vem do backend. */
export const establishmentHasCustomThemeColors = (
  primaryColor?: string | null,
  secondaryColor?: string | null
): boolean => Boolean(primaryColor?.trim() && secondaryColor?.trim());

/** Par primário+secundário igual ao default de produto Hivi (tenant sem personalização de cor). */
export const establishmentColorsMatchHiviProductDefaults = (
  primary: string,
  secondary: string
): boolean => {
  const p = primary.trim().toLowerCase();
  const s = secondary.trim().toLowerCase();
  return (
    p === DEFAULT_BRAND_COLORS.primary.toLowerCase() &&
    s === DEFAULT_BRAND_COLORS.secondary.toLowerCase()
  );
};

/** Canais `r, g, b` para `rgba(var(--color-*-rgb), α)` no Tailwind; fallback Hivi royal. */
export const establishmentHexToRgbChannels = (hex: string): string => {
  const normalized = hex.trim();

  const short = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(normalized);
  if (short) {
    return `${parseInt(short[1] + short[1], 16)}, ${parseInt(short[2] + short[2], 16)}, ${parseInt(short[3] + short[3], 16)}`;
  }

  const full = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(normalized);
  if (full) {
    return `${parseInt(full[1], 16)}, ${parseInt(full[2], 16)}, ${parseInt(full[3], 16)}`;
  }

  return '0, 88, 229';
};

/** Garante que as CSS vars da marca usem a mesma paleta dos defaults em TypeScript. */
export const applyDefaultBrandCssVariables = (root: HTMLElement = document.documentElement) => {
  Object.entries(DEFAULT_BRAND_CSS_VARIABLES).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
};
